import { Copy, Key, Pencil, Trash } from 'lucide-react';
import * as React from 'react';
import styled from 'styled-components';
import Input from '../../../components_v2/input/Input';
import { isSuperAdmin } from '../../../components_v2/utils';
import AntropicIcon from '../../../styles/custom-icons/antropic';
import { CustomIconProperties } from '../../../styles/custom-icons/custom-icons';
import MistralIcon from '../../../styles/custom-icons/mistral';
import OpenAiIcon from '../../../styles/custom-icons/open-ai';
import { Dot } from '../../../styles/global/css/Dot';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { BorderColor, DarkGreySidely2, GreenSidely, OrangeSidely, RedSidely } from '../../../styles/global/css/Utils';
import ProgressBar from '../../../styles/global/loader/Loader';
import { Translate, translateToString } from '../../../styles/global/translate';
import { AlertRes } from '../../alert/AlertProvider';
import useAlert, { useDeletionAlert } from '../../alert/UseAlert';
import { NoMarginH6, NoMarginP } from '../../authorization/users/Members';
import { BoolCheckbox } from '../../forms/PopupField';
import { launchToastMessage } from '../../forms/visual-form-editor/visualFormEditor';
import { ComponentProps } from '../../globals/mainPage/mainPage';
import { FlexDiv } from '../../products/style';
import { AiApiKey, ApiToken, ApiUtilisation, deleteAiApiKeys, deleteApiToken, getAiApiKeys, getApiTokens, getApiUtilisation, getVisionSettings, postAiApiKeys, postApiTokens, putAiApiKeys, putVisionInternalAiKey, VisionSettings } from './actions';
import { INPUT_STYLE_SETTINGS, SystemSettingsDropdownWrapper, SystemSettingsWrapper, SystemSettingTitle } from './styles';

const ApiKeyButton = styled(DefaultButton)`
	width: fit-content;
  	display: flex;
  	justify-content: center;
  	align-items: center;
  	gap: 5px;
  	margin: 0;
`;

const ApiTokenGrid = styled.div`
	display: grid;
	grid-template-columns: 350px 150px 25px 25px;
	gap: 10px;
	align-items: center;

	& > * {
		margin: auto;
	}
`;

const AiApiKeyGrid = styled.div`
	display: grid;
	grid-template-columns: 200px 200px 100px 25px 25px;
	gap: 10px;
	align-items: center;

	& > * {
		margin: auto;
	}
`;

const AiProviderCard = styled.div`
	display: flex;
	flex-flow: column;
	align-items: flex-start;
	gap: 10px;
	border: 1px solid ${BorderColor};
	border-radius: 5px;
	padding: 15px;
	width: fit-content;
`;

type AiApiKeyBody = Omit<AiApiKey, 'id'>;

type AiProvider = {
	/** value stored in database, parsed by `AiProvider::from` on the backend */
	value: string,
	label: string,
	Icon: (props: CustomIconProperties) => JSX.Element
};

const AI_PROVIDERS: AiProvider[] = [
	{ value: 'anthropic', label: 'Anthropic', Icon: AntropicIcon },
	{ value: 'openai', label: 'OpenAI', Icon: OpenAiIcon },
	{ value: 'mistral', label: 'Mistral', Icon: MistralIcon }
];

function AiApiKeyPopup(props: {
	provider: AiProvider,
	apiKey?: AiApiKey,
	onOk: (body: AiApiKeyBody) => void
}) {
	const { provider, apiKey } = props;
	const [name, setName] = React.useState<string>(apiKey?.name ?? '');
	const [description, setDescription] = React.useState<string>(apiKey?.description ?? '');
	const [value, setValue] = React.useState<string>('');
	const [isDefault, setIsDefault] = React.useState<boolean>(apiKey?.is_default ?? false);
	const isValid = name.trim().length > 0 && (apiKey !== undefined || value.trim().length > 0);

	return <FlexDiv flow='column' align='stretch' gap='20px' width='100%'>
		<FlexDiv gap='1ch'>
			<provider.Icon size={24} />
			<NoMarginH6>{provider.label}</NoMarginH6>
		</FlexDiv>
		<FlexDiv flow='column' align='stretch' gap='30px' width='100%'>
			<Input
				name='ai_api_key_name'
				type='text'
				label={<Translate id='name' />}
				value={name}
				onChange={setName}
				inputStyle={{ ...INPUT_STYLE_SETTINGS, containerWidth: '100%' }}
				required
				autoFocus
			/>
			<Input
				name='ai_api_key_description'
				type='text'
				label={<Translate id='description' />}
				value={description}
				onChange={setDescription}
				inputStyle={{ ...INPUT_STYLE_SETTINGS, containerWidth: '100%' }}
			/>
			<Input
				name='ai_api_key_value'
				type={apiKey ? 'text' : 'password'}
				label={<Translate id='ai_api_key_value' />}
				value={apiKey ? apiKey.value : value}
				onChange={setValue}
				inputStyle={{ ...INPUT_STYLE_SETTINGS, containerWidth: '100%' }}
				disabled={apiKey !== undefined}
				required={apiKey === undefined}
			/>
			<FlexDiv onClick={() => setIsDefault(!isDefault)} cursor='pointer' gap='1ch'>
				<BoolCheckbox type='checkbox' checked={isDefault} onChange={() => { }} />
				<NoMarginP><Translate id='ai_api_key_default' /></NoMarginP>
			</FlexDiv>
		</FlexDiv>

		<ApiKeyButton
			disabled={!isValid}
			onClick={() => props.onOk({
				name: name.trim(),
				description: description.trim() || undefined,
				value: apiKey?.value ?? value.trim(),
				provider: provider.value,
				is_default: isDefault
			})}
		>
			<Key color='white' /><Translate id={apiKey ? 'save' : 'create'} />
		</ApiKeyButton>
	</FlexDiv>;
}

function AiApiKeyProvider(props: {
	provider: AiProvider,
	apiKeys: AiApiKey[],
	refresh: () => void
}) {
	const { provider, apiKeys, refresh } = props;
	const alert = useAlert();
	const alertDelete = useDeletionAlert();

	async function upsert(apiKey?: AiApiKey) {
		const res = await alert<AiApiKeyBody>({
			title: <Translate id={apiKey ? 'ai_api_key_edit' : 'ai_api_key_create'} />,
			content: (_, onOk) => <AiApiKeyPopup provider={provider} apiKey={apiKey} onOk={onOk} />,
			noButtons: true
		});
		if (res.res === AlertRes.Break) return;
		await (apiKey
			? putAiApiKeys(apiKey.id, { name: res.data.name, description: res.data.description, is_default: res.data.is_default })
			: postAiApiKeys(res.data));
		refresh();
	}

	async function remove(apiKey: AiApiKey) {
		const res = await alertDelete({ name: apiKey.name });
		if (res.res !== AlertRes.Ok) return;
		await deleteAiApiKeys(apiKey.id);
		refresh();
	}

	return <AiProviderCard>
		<FlexDiv gap='1ch'>
			<provider.Icon size={24} />
			<NoMarginH6>{provider.label}</NoMarginH6>
		</FlexDiv>
		{apiKeys.length === 0
			? <NoMarginP style={{ color: DarkGreySidely2 }}><Translate id='ai_api_key_none' /></NoMarginP>
			: <AiApiKeyGrid>
				<p><Translate id='name' /></p>
				<p><Translate id='ai_api_key_value' /></p>
				<p style={{ textAlign: 'center' }}><Translate id='ai_api_key_default' /></p>
				<span />
				<span />
				{apiKeys.map(apiKey => <React.Fragment key={apiKey.id}>
					<p title={apiKey.description}>{apiKey.name}</p>
					<p>{apiKey.value}</p>
					<Dot color={apiKey.is_default ? GreenSidely : RedSidely} />
					<Pencil cursor='pointer' size={20} onClick={() => upsert(apiKey)} />
					<Trash color={RedSidely} cursor='pointer' size={20} onClick={() => remove(apiKey)} />
				</React.Fragment>)}
			</AiApiKeyGrid>}
		<ApiKeyButton onClick={() => upsert()}>
			<Key color='white' /><Translate id='ai_api_key_create' />
		</ApiKeyButton>
	</AiProviderCard>;
}

export default function ApiConsumption(props: ComponentProps) {
	const [apiTokens, setApiTokens] = React.useState<ApiToken[]>();
	const [apiUtilisation, setApiUtilisation] = React.useState<ApiUtilisation[]>();
	const [aiApiKeys, setAiApiKeys] = React.useState<AiApiKey[]>();
	// superadmins decide whether this company may run Vision readings on Sidely's own AI key
	const superAdmin = isSuperAdmin();
	const [visionSettings, setVisionSettings] = React.useState<VisionSettings>();
	const alert = useAlert();

	const refreshAiApiKeys = () => getAiApiKeys().then(setAiApiKeys);

	React.useEffect(() => {
		getApiTokens().then(setApiTokens);
		getApiUtilisation().then(setApiUtilisation);
		refreshAiApiKeys();
		if (superAdmin) getVisionSettings().then(setVisionSettings).catch(() => setVisionSettings(undefined));
		props.setToolBarState({});
	}, []);

	return <SystemSettingsWrapper>
		<SystemSettingTitle><Translate id='api_consumption' /></SystemSettingTitle>
		<SystemSettingsDropdownWrapper>
			{apiUtilisation?.length === 0 && <p>No API Utilisation Data</p>}
			{apiUtilisation?.map((utilisation) => {
				const progress = utilisation.utilisation_limit ? utilisation.utilisation / utilisation.utilisation_limit : undefined;
				return (
					<FlexDiv key={utilisation.route} flow='column' align='stretch' gap='5px'>
						<NoMarginH6>{utilisation.route}</NoMarginH6>
						{progress !== undefined && <ProgressBar progress={progress} showPercentage color={progress > 1 ? RedSidely : progress > 0.666 ? OrangeSidely : GreenSidely} height={25} />}
						{utilisation.models.map(model => {
							const keyName = model.api_key_id !== null ? aiApiKeys?.find(k => k.id === model.api_key_id)?.name ?? `#${model.api_key_id}` : 'Sidely';
							return <NoMarginP key={`${model.provider}-${model.model}-${model.api_key_id}`}>
								{model.provider} · {model.model} · {keyName}: {model.input_tokens} input + {model.output_tokens} output tokens
							</NoMarginP>;
						})}
					</FlexDiv>
				);
			})}
		</SystemSettingsDropdownWrapper>
		<SystemSettingTitle><Translate id='api_key' /></SystemSettingTitle>
		<SystemSettingsDropdownWrapper>
			<ApiTokenGrid>
				<p><Translate id='api_key' /></p>
				<p style={{ textAlign: 'center' }}><Translate id='writting_allowed' /></p>
				<span />
				<span />
				{apiTokens?.map((token) => <React.Fragment key={token.token}>
					<Input name={token.token} type='password' value={token.token} disabled />
					<Dot color={token.allow_write ? GreenSidely : RedSidely} />
					<Copy size={20} style={{ cursor: 'pointer' }} onClick={() => {
						navigator.clipboard.writeText(token.token);
						launchToastMessage(translateToString('copied_in_clipboard'));
					}} />
					<Trash color={RedSidely} cursor='pointer' onClick={() => deleteApiToken(token.token).then(_ => setApiTokens(apiTokens.filter(t => t.token !== token.token)))} />
				</React.Fragment>)}
			</ApiTokenGrid>
			<ApiKeyButton onClick={async() => {
				const res = await alert<boolean>({
					title: <Translate id='create_an_api_key' />,
					content: (_, onOk) => {
						const [writtingAllowed, setWrittingAllowed] = React.useState(false);
						return <>
							<FlexDiv onClick={() => setWrittingAllowed(!writtingAllowed)} cursor='pointer' gap='1ch'>
								<BoolCheckbox type={'checkbox'} checked={writtingAllowed} onChange={() => { }} />
								<NoMarginP><Translate id='writting_allowed' /></NoMarginP>
							</FlexDiv>
							<span />
							<ApiKeyButton onClick={() => onOk(writtingAllowed)}><Key color='white' /><Translate id='create_an_api_key' /></ApiKeyButton>
						</>;
					},
					noButtons: true,
				});
				if (res.res === AlertRes.Break) return;
				postApiTokens(res.data).then(token => setApiTokens([...(apiTokens ?? []), { token, allow_write: res.data }]));
			}}>
				<Key color='white' /><Translate id='create_an_api_key' />
			</ApiKeyButton>
		</SystemSettingsDropdownWrapper>
		<SystemSettingTitle><Translate id='ai_api_key' /></SystemSettingTitle>
		<SystemSettingsDropdownWrapper>
			<FlexDiv gap='1rem' align='flex-start' flexWrap='wrap'>
				{AI_PROVIDERS.map(provider => <AiApiKeyProvider
					key={provider.value}
					provider={provider}
					apiKeys={aiApiKeys?.filter(key => key.provider === provider.value) ?? []}
					refresh={refreshAiApiKeys}
				/>)}
			</FlexDiv>
			{superAdmin && visionSettings && <FlexDiv flow='column' align='stretch' gap='5px' style={{ marginTop: '1rem' }}>
				<FlexDiv cursor='pointer' gap='1ch' onClick={() => {
					const enabled = !visionSettings.internal_ai_key;
					putVisionInternalAiKey(enabled)
						.then(() => setVisionSettings({ ...visionSettings, internal_ai_key: enabled }))
						.catch(() => launchToastMessage(translateToString('vision_internal_ai_key_failed')));
				}}>
					<BoolCheckbox type='checkbox' checked={visionSettings.internal_ai_key} onChange={() => { }} />
					<NoMarginP><Translate id='vision_internal_ai_key' /></NoMarginP>
				</FlexDiv>
				<NoMarginP style={{ color: DarkGreySidely2 }}><Translate id='vision_internal_ai_key_hint' /></NoMarginP>
			</FlexDiv>}
		</SystemSettingsDropdownWrapper>
	</SystemSettingsWrapper>;
}
